import { createReducer } from '@reduxjs/toolkit';
import { BatchDto, GoodsDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewEmptyGoodsBatchEntity,
    clearGoods,
    saveGoods,
    updateGoodsArticle,
    updateGoodsQuantity,
    updateGoodsSite,
    updateGoodsSiteId,
    updateGoodsStatus,
    updateBatchArrivalTime,
    updateBatchClient,
    updateBatchPrice,
    updateBatchUniqueId,
    removeGoodsBatchEntity,
} from '../actions/goodsActions';
import { clearBaseEntityState, IBaseEntityState, SaveEntityStatus } from './common';

export type GoodBatchEntity = { index: number; goods: GoodsDto; batch: BatchDto };

export interface GoodsToSiteFormReducerState extends IBaseEntityState {
    goodBatchEntities: GoodBatchEntity[] | null;
}

function clearState(state: GoodsToSiteFormReducerState): void {
    state.goodBatchEntities = null;

    clearBaseEntityState(state);
}

function getInitialState(): GoodsToSiteFormReducerState {
    const state: GoodsToSiteFormReducerState = {
        goodBatchEntities: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

export const goodsToSiteFormReducer = createReducer<GoodsToSiteFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(createNewEmptyGoodsBatchEntity, (state: GoodsToSiteFormReducerState, action: any) => {
                if (!state.goodBatchEntities || state.goodBatchEntities.length <= 0) {
                    const index = 1;

                    const newGoodBatch: GoodBatchEntity = {
                        index: index,
                        batch: new BatchDto({ batchArrivalTime: new Date() }),
                        goods: new GoodsDto(),
                    };

                    state.goodBatchEntities = [newGoodBatch];
                } else {
                    const index = state.goodBatchEntities[state.goodBatchEntities.length - 1].index + 1;

                    const lastElement = state.goodBatchEntities[state.goodBatchEntities?.length - 1];

                    const newGoodBatch: GoodBatchEntity = {
                        index: index,
                        batch: new BatchDto({
                            batchArrivalTime: lastElement.batch.batchArrivalTime,
                            supplierId: lastElement.batch.supplierId,
                            supplier: lastElement.batch.supplier,
                            uniqueBatchId: lastElement.batch.uniqueBatchId,
                        }),
                        goods: new GoodsDto(),
                    };

                    state.goodBatchEntities = [...state.goodBatchEntities, newGoodBatch];
                }
            })
            .addCase(clearGoods, (state: GoodsToSiteFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(removeGoodsBatchEntity, (state: GoodsToSiteFormReducerState, action: any) => {
                if (!!state.goodBatchEntities) {
                    const index = action.payload;
                    if (state.goodBatchEntities.length === 0) {
                        state.goodBatchEntities = [];
                    } else {
                        const goodIndex = state.goodBatchEntities.findIndex((g) => g.index === index);
                        if (goodIndex > -1) {
                            state.goodBatchEntities.splice(goodIndex, 1);
                        }
                    }
                }
            })
            .addCase(updateGoodsArticle, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, article } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.goods.article = article;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(updateGoodsStatus, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, goodsStatus } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.goods.goodsStatus = goodsStatus;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(updateGoodsQuantity, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, quantity } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.goods.quantity = quantity;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(updateGoodsSiteId, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, siteId } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.goods.siteId = siteId;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(updateGoodsSite, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, site } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.goods.site = site;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(updateBatchPrice, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, batchPrice } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.batch.purchasePricePerArticle = batchPrice;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(updateBatchUniqueId, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, uniqueBatchId } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.batch.uniqueBatchId = uniqueBatchId;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(updateBatchClient, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, batchClient } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.batch.supplier = batchClient;
                    newGood.batch.supplierId = batchClient?.id;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(updateBatchArrivalTime, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if it is being updated');

                const { index, batchArrivalTime } = action.payload;

                const newGoodIndex = state.goodBatchEntities?.findIndex((g) => g.index === index);

                if (newGoodIndex !== undefined && newGoodIndex > -1 && !!state.goodBatchEntities) {
                    const newGood = state.goodBatchEntities[newGoodIndex];

                    newGood.batch.batchArrivalTime = batchArrivalTime;

                    state.goodBatchEntities = [
                        ...state.goodBatchEntities.slice(0, newGoodIndex),
                        newGood,
                        ...state.goodBatchEntities.slice(newGoodIndex + 1),
                    ];
                }
            })
            .addCase(saveGoods.pending, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveGoods.fulfilled, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;
            })
            .addCase(saveGoods.rejected, (state: GoodsToSiteFormReducerState, action: any) => {
                myAssert(() => state.goodBatchEntities !== null, 'There needs to be goods if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
